import type { NexxusDatabase } from '@infra/persistence/NexxusDatabase';
import {
  DEXIE_MIGRATION_TABLES,
  type DexieMigrationTable,
} from '@infra/persistence/Migrations/dexie_to_sqlite';
import { SqliteEntityBridge } from '@infra/persistence/SqliteEntityBridge';
import { RemoteSyncStore } from '@infra/sync/RemoteSyncStore';

const CLOUD_TABLE_RESOURCES: Partial<Record<DexieMigrationTable, string>> = {
  salaries: 'salaries',
  bulletins: 'bulletins',
  ecritures: 'ecritures',
  factures: 'factures',
  centres_analytiques: 'centres-analytiques',
};

let cloudTablesEnabled = false;
let cloudTenantId = 'default';

/** Active la lecture cache serveur pour les tables métier en mode Cloud. */
export function configureLocalTableStoreForCloud(enabled: boolean, tenantId: string): void {
  cloudTablesEnabled = enabled;
  cloudTenantId = tenantId;
}

export function getLocalTableStoreCloudTenantId(): string {
  return cloudTenantId;
}

type DexieTable = {
  toArray: () => Promise<unknown[]>;
  get: (id: string) => Promise<unknown>;
  put: (row: unknown) => Promise<unknown>;
  delete: () => Promise<void>;
  where: (index: string) => { equals: (v: string) => { count: () => Promise<number> } };
};

/** Accès unifié Dexie / SQLite chiffré pour les tables métier restantes. */
export class LocalTableStore {
  constructor(private readonly db: NexxusDatabase) {}

  private dexie(name: DexieMigrationTable): DexieTable {
    return (this.db as unknown as Record<string, DexieTable>)[name]!;
  }

  private async sqlitePrimary(): Promise<boolean> {
    return SqliteEntityBridge.isSqlitePrimary();
  }

  /**
   * Cloisonnement dossier (équivalent clés React Query + dossierId).
   * - `tenants` reste global (portefeuille Cabinet).
   * - Autres tables : scope = argument explicite, sinon `cloudTenantId`
   *   même en standalone (pas seulement si cache Cloud actif).
   */
  private activeTenant(
    table: DexieMigrationTable,
    explicit?: string,
  ): string | undefined {
    if (explicit) return explicit;
    if (table === 'tenants') return undefined;
    if (cloudTenantId && cloudTenantId !== 'default') return cloudTenantId;
    return undefined;
  }

  async list<T extends { id: string; tenant_id?: string }>(
    table: DexieMigrationTable,
    tenantId?: string,
  ): Promise<T[]> {
    const scope = this.activeTenant(table, tenantId);
    const cloudRows = await this.listFromCloudCache<T>(table, scope);
    if (cloudRows !== null) return cloudRows;
    if (await this.sqlitePrimary()) {
      const rows =
        scope && table !== 'tenants'
          ? await SqliteEntityBridge.list<T>(table, scope)
          : await SqliteEntityBridge.list<T>(table);
      if (table === 'tenants') {
        return rows;
      }
      if (scope) {
        // T-01 — pas d’orphelins sans tenant_id (fuite multi-dossier).
        return rows.filter((r) => r.tenant_id === scope);
      }
      return rows;
    }
    const all = (await this.dexie(table).toArray()) as T[];
    if (table === 'tenants') {
      return all;
    }
    if (!scope) return all;
    return all.filter((r) => r.tenant_id === scope);
  }

  /**
   * En Cloud, un cache vide est une réponse valide (entreprise neuve),
   * pas un prétexte pour relire Dexie (données d’une autre société).
   */
  private async listFromCloudCache<T extends { id: string; tenant_id?: string }>(
    table: DexieMigrationTable,
    tenantId?: string,
  ): Promise<T[] | null> {
    const resource = CLOUD_TABLE_RESOURCES[table];
    if (!cloudTablesEnabled || !resource || !(await RemoteSyncStore.isEnabled())) return null;
    const scope = tenantId ?? cloudTenantId;
    const rows = await RemoteSyncStore.listCache<T>(scope, resource);
    // T-01 — cloisonnement strict (refus des lignes sans tenant_id).
    return rows.filter((r) => r.tenant_id === scope);
  }

  async get<T extends { id: string; tenant_id?: string }>(
    table: DexieMigrationTable,
    id: string,
  ): Promise<T | null> {
    const resource = CLOUD_TABLE_RESOURCES[table];
    if (cloudTablesEnabled && resource && (await RemoteSyncStore.isEnabled())) {
      const cached = await RemoteSyncStore.getCache<T>(cloudTenantId, resource, id);
      // Miss cache Cloud = absence réelle — ne pas relire Dexie (autre société).
      return cached ? this.scopedRow(table, cached) : null;
    }
    if (await this.sqlitePrimary()) {
      return this.scopedRow(table, await SqliteEntityBridge.get<T>(table, id));
    }
    const row = ((await this.dexie(table).get(id)) as T | undefined) ?? null;
    return this.scopedRow(table, row);
  }

  private scopedRow<T extends { id: string; tenant_id?: string }>(
    table: DexieMigrationTable,
    row: T | null,
  ): T | null {
    if (!row) return null;
    if (table === 'tenants') {
      return !cloudTenantId || cloudTenantId === 'default' || row.id === cloudTenantId ? row : null;
    }
    // T-01 — refus cross-dossier et orphelins sans tenant_id.
    if (cloudTenantId && cloudTenantId !== 'default') {
      if (!row.tenant_id || row.tenant_id !== cloudTenantId) return null;
    }
    return row;
  }

  async put<T extends { id: string; tenant_id?: string }>(
    table: DexieMigrationTable,
    row: T,
  ): Promise<void> {
    if (table === 'tenants') {
      if (cloudTenantId && cloudTenantId !== 'default' && row.id !== cloudTenantId) {
        return;
      }
    } else if (cloudTenantId && cloudTenantId !== 'default') {
      if (!row.tenant_id || row.tenant_id !== cloudTenantId) {
        return;
      }
    }
    if (await this.sqlitePrimary()) {
      await SqliteEntityBridge.put(table, row);
      return;
    }
    await this.dexie(table).put(row);
  }

  async countByTenant(table: DexieMigrationTable, tenantId: string): Promise<number> {
    if (table === 'tenants') {
      const t = await this.get(table, tenantId);
      return t ? 1 : 0;
    }
    if (cloudTablesEnabled && (await RemoteSyncStore.isEnabled())) {
      const rows = await this.listFromCloudCache(table, tenantId);
      if (rows !== null) return rows.length;
    }
    if (await this.sqlitePrimary()) {
      return SqliteEntityBridge.count(table, tenantId);
    }
    return this.dexie(table).where('tenant_id').equals(tenantId).count();
  }

  async resetAll(): Promise<void> {
    if (await SqliteEntityBridge.isAvailable()) {
      await SqliteEntityBridge.clearAllTenants();
    }
    await this.db.delete();
  }
}

export async function createLocalTableStore(db: NexxusDatabase): Promise<LocalTableStore> {
  return new LocalTableStore(db);
}

export { DEXIE_MIGRATION_TABLES };
